import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { HandymanAuthProvider, useHandymanAuth } from "./context/HandymanAuthContext";
import { Layout } from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";
import { Home } from "./pages/Home";
import { Services } from "./pages/Services";
import { About } from "./pages/About";
import { Gallery } from "./pages/Gallery";
import { Login, Register } from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { Quote } from "./pages/Quote";
import { Booking } from "./pages/Booking";
import { Dashboard } from "./pages/Dashboard";
import { HandymanApply } from "./pages/HandymanApply";
import { HandymanLogin } from "./pages/HandymanLogin";
import { HandymanDashboard } from "./pages/HandymanDashboard";
import { AdminDashboard } from "./pages/AdminDashboard";
import { Sitemap } from "./pages/Sitemap";
import { Toaster } from "./components/ui/sonner";
import { CookieConsent } from "./components/CookieConsent";
import { MobileActionBar } from "./components/MobileActionBar";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Protected Route component for customers
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Protected Route component for handymen
const HandymanProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useHandymanAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/handyman/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <ErrorBoundary>
      <ScrollToTop />
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/about" element={<About />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/quote" element={<Quote />} />
          <Route 
          path="/booking/:quoteId" 
          element={
            <ProtectedRoute>
              <Booking />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        {/* Handyman Routes */}
        <Route path="/handyman/apply" element={<HandymanApply />} />
        <Route path="/handyman/login" element={<HandymanLogin />} />
        <Route 
          path="/handyman/dashboard" 
          element={
            <HandymanProtectedRoute>
              <HandymanDashboard />
            </HandymanProtectedRoute>
          } 
        />
        {/* Admin Route */}
        <Route path="/admin" element={<AdminDashboard />} />
        {/* Sitemap */}
        <Route path="/sitemap" element={<Sitemap />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <HandymanAuthProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
            <CookieConsent />
            <MobileActionBar />
          </HandymanAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
